import type { ReactElement } from 'react';
import { Children } from 'react';
import type { RouteProps } from 'react-router';
import { Redirect, matchPath } from 'react-router';
import RouteError from '../../../routing/RouteError';
import useLocation from '../../hooks/useLocation';
import { getCurrentUser } from '../../services/CurrentUser';
import { sessionStorage } from '../../services/SafeBrowserStorage';

type RouteElement = ReactElement<RouteProps>;

interface Props {
    children: Array<RouteElement | boolean>;
    defaultPath: string;
    /** By default, defaultPath will be used for the storage key. This lets you override it for exceptions. */
    keySuffixOverride?: string;
    excludedPaths?: string[];
}

export default function SwitchDefaultRoute({ children, defaultPath, keySuffixOverride, excludedPaths = [] }: Props) {
    const location = useLocation();

    const key = getKey(keySuffixOverride ?? defaultPath);
    if (keySuffixOverride) {
        const outdatedKey = getKey(defaultPath);
        const outdatedItem = sessionStorage.getItem(outdatedKey);
        if (outdatedItem) {
            sessionStorage.removeItem(outdatedKey);
        }
    }
    const childArray = Children.toArray(children) as Array<RouteElement>;

    if (matchPath(location.pathname, { path: defaultPath, exact: true })) {
        const lastPath = sessionStorage.getItem(key);
        const validLastPath = childArray.some(child => child.props.path === lastPath) ? lastPath : undefined;
        const redirectPath = validLastPath || childArray[0].props.path;
        return <Redirect to={redirectPath} />;
    }

    const result = childArray.find(element => {
        const { path, exact, strict } = element.props;
        return matchPath(location.pathname, { path, exact, strict });
    });

    if (result) {
        const { path } = result.props;
        const pathString = path?.toString() || '';
        if (!pathString.includes(':') && !excludedPaths.includes(pathString)) {
            sessionStorage.setItem(key, path);
        }
        return result;
    }

    sessionStorage.removeItem(key);
    return <RouteError errorCode={404} />;
}

function getKey(path: string) {
    return 'SwitchDefaultRoute_' + getCurrentUser().id + '_' + path;
}
